import { ChangeDetectionStrategy, Component, HostBinding, inject, Input } from '@angular/core';
import { IconData } from './icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconService } from './icon.service';

export type IconSize = 'xxs' | 'xs' | 'sm' | 'smm' | 'md' | 'lg' | 'mlg' | 'xl' | 'xxl' | 'original';
export type SvgStroke = 'green' | 'yellow' | 'gray' | 'red' | 'blue' | 'white';

@Component({
  selector: 'app-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly iconService = inject(IconService);

  @Input() set name(iconName: string) {
    this.generateIcon(iconName);
  }
  @Input() size: IconSize;
  @Input() svgStroke: SvgStroke;

  @HostBinding('class.xsmall')
  get original(): boolean {
    return this.size === 'original';
  }

  @HostBinding('class.xxsmall')
  get xxsmall(): boolean {
    return this.size === 'xxs';
  }

  @HostBinding('class.xsmall')
  get xsmall(): boolean {
    return this.size === 'xs';
  }

  @HostBinding('class.small')
  get small(): boolean {
    return this.size === 'sm';
  }

  @HostBinding('class.medium')
  get medium(): boolean {
    return this.size === 'md';
  }

  @HostBinding('class.large')
  get large(): boolean {
    return this.size === 'lg';
  }

  @HostBinding('class.svg-green')
  get green(): boolean {
    return this.svgStroke === 'green';
  }

  @HostBinding('class.svg-blue')
  get blue(): boolean {
    return this.svgStroke === 'blue';
  }

  @HostBinding('class.svg-gray')
  get gray(): boolean {
    return this.svgStroke === 'gray';
  }

  @HostBinding('class.svg-yellow')
  get yellow(): boolean {
    return this.svgStroke === 'yellow';
  }

  @HostBinding('class.svg-red')
  get red(): boolean {
    return this.svgStroke === 'red';
  }

  @HostBinding('class.svg-white')
  get white(): boolean {
    return this.svgStroke === 'white';
  }

  @HostBinding('class.small-m')
  get smallm(): boolean {
    return this.size === 'smm';
  }

  @HostBinding('class.mlarge')
  get mlarge(): boolean {
    return this.size === 'mlg';
  }

  @HostBinding('class.xlarge')
  get xlarge(): boolean {
    return this.size === 'xl';
  }

  @HostBinding('class.xxlarge')
  get xxlarge(): boolean {
    return this.size === 'xxl';
  }

  resultingSvg: SafeHtml;
  private iconData: IconData;

  generateIcon(iconName: string): void {
    if (!iconName) {
      return;
    }
    this.iconData = this.iconService.getIconData(iconName.toLocaleLowerCase());
    const resultingSvg = `<div class='icon-item'>${(this.iconData && this.iconData.svg) || ''}</div>`;
    this.resultingSvg = this.sanitizer.bypassSecurityTrustHtml(resultingSvg);
  }
}
